import ms from 'ms';
import * as z from 'zod';
import { useFormContext } from 'react-hook-form';
import { AlertTriangle } from 'lucide-react';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

export const MINUTE_MS = 60_000;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;

export const safeMs = value => {
  if (typeof value !== 'string' || value.trim() === '') return undefined;
  try {
    return ms(value);
  } catch {
    return undefined;
  }
};

export const durationMs = ({ min, max, minLabel, maxLabel } = {}) =>
  z.string().superRefine((value, ctx) => {
    const parsed = safeMs(value);
    if (parsed === undefined) {
      return ctx.addIssue({
        code: 'custom',
        message: 'Enter a duration like "30d", "12h", or "0.5d"',
      });
    }
    if (min !== undefined && max !== undefined && (parsed < min || parsed > max)) {
      ctx.addIssue({
        code: 'custom',
        message: `Must be between ${minLabel ?? ms(min, { long: true })} and ${maxLabel ?? ms(max, { long: true })}`,
      });
    }
  });

export default function DurationField({
  name,
  label,
  description,
  note,
  placeholder = '30d',
  disabled = false,
}) {
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const parsed = safeMs(field.value);
        const hint = parsed === undefined ? null : ms(parsed, { long: true });
        return (
          <FormItem>
            <FormLabel className="font-bold">{label}</FormLabel>
            <FormControl>
              <Input placeholder={placeholder} disabled={disabled} {...field} />
            </FormControl>
            {hint && <p className="text-xs text-muted-foreground">= {hint}</p>}
            {description && <FormDescription>{description}</FormDescription>}
            {note && (
              <p className="flex items-center gap-1.5 text-xs font-medium text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                {note}
              </p>
            )}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
